<template>
  <div>
    <b-form-group
      label="Référence du colis échange livré"
      label-for="exchange-scan-input"
    >
      <b-input-group class="input-group-merge">
        <b-input-group-prepend is-text>
          <feather-icon icon="MaximizeIcon" />
        </b-input-group-prepend>
        <b-form-input
          id="exchange-scan-input"
          v-model="ref"
          :disabled="isScanningExchange"
          placeholder="Saisir ou scanner la référence du colis échange"
          @change="checkOrder"
        />
      </b-input-group>
    </b-form-group>

    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-1">
        <span>Colis scannés : <strong>{{ exchangeScannedOrders.length }}</strong></span>
        <b-button
          variant="outline-secondary"
          @click="$router.push('/bons-echanges')"
        >
          <feather-icon icon="CheckIcon" class="mr-50" />
          Terminer
        </b-button>
      </div>

      <vue-good-table
        :columns="columns"
        :rows="exchangeScannedOrders"
        :search-options="{ enabled: true, externalQuery: searchTerm }"
      >
        <template slot="table-row" slot-scope="props">
          <!-- Source order column -->
          <span v-if="props.column.field === 'source_order_id'">
            <span>{{ props.row.source_order_display_id || props.row.source_order_id }}</span>
          </span>

          <!-- Generated order column -->
          <span v-else-if="props.column.field === 'generated_order'">
            <span v-if="props.row.generated_order">
              {{ props.row.generated_order.display_cross_tenant_barcode && props.row.generated_order.cross_tenant_barcode
                ? props.row.generated_order.cross_tenant_barcode
                : props.row.generated_order.id }}
              <br>
              <small class="text-muted">{{ props.row.generated_order.name }}</small>
            </span>
          </span>

          <!-- Voucher ID column -->
          <span v-else-if="props.column.field === 'exchange_voucher_id'">
            <b-badge
              variant="light-primary"
              class="cursor-pointer"
              @click="$router.push(`/bons-echanges/show/${props.row.exchange_voucher_id}`)"
            >
              #{{ props.row.exchange_voucher_id }}
            </b-badge>
          </span>

          <!-- Action column -->
          <span v-else-if="props.column.field === 'action'">
            <feather-icon
              :id="`exchange-row-${props.row.source_order_id}-delete`"
              icon="EyeIcon"
              class="cursor-pointer mr-1"
              size="16"
              @click="$router.push(`/bons-echanges/show/${props.row.exchange_voucher_id}`)"
            />
            <b-tooltip
              title="Voir le bon d'échange"
              :target="`exchange-row-${props.row.source_order_id}-delete`"
            />
          </span>

          <span v-else>{{ props.formattedRow[props.column.field] }}</span>
        </template>

        <template slot="pagination-bottom" slot-scope="props">
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap">Affichage 1 à</span>
              <b-form-select
                v-model="pageLength"
                :options="['50','100','500']"
                class="mx-1"
                @input="(value) => props.perPageChanged({ currentPerPage: value })"
              />
              <span class="text-nowrap"> de {{ props.total }} résultats</span>
            </div>
            <div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BButton, BPagination, BFormGroup, BFormInput, BInputGroup,
  BInputGroupPrepend, BFormSelect, BTooltip, BBadge,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ok from '@/assets/audio/ok.mp3'
import notexist from '@/assets/audio/notexist.mp3'
import alreadyScanned from '@/assets/audio/colis-deja-scanne.mp3'

export default {
  components: {
    BCard,
    BButton,
    VueGoodTable,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormSelect,
    BPagination,
    BTooltip,
    BBadge,
  },
  data() {
    return {
      ref: '',
      isScanningExchange: false,
      pageLength: 100,
      searchTerm: '',
      exchangeScannedOrders: [],
      ok,
      notexist,
      alreadyScanned,
      columns: [
        {
          label: 'Action',
          field: 'action',
          width: '80px',
          sortable: false,
        },
        {
          label: 'Colis échange (source)',
          field: 'source_order_id',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher référence source',
          },
        },
        {
          label: 'Colis retour généré',
          field: 'generated_order',
          filterOptions: {
            enabled: false,
          },
        },
        {
          label: "Bon d'échange",
          field: 'exchange_voucher_id',
          filterOptions: {
            enabled: true,
            placeholder: "Chercher bon d'échange",
          },
        },
      ],
    }
  },
  methods: {
    playAudio(audio) {
      const voice = new Audio(audio)
      voice.addEventListener('canplaythrough', () => { voice.play() })
    },

    async withTitle(text) {
      try {
        await this.$swal({
          title: 'Alerte',
          icon: 'warning',
          text,
          customClass: {
            confirmButton: 'btn btn-primary',
          },
          buttonsStyling: false,
        })
      } catch (e) { /* dismissed */ }
      this.ref = ''
    },

    async checkOrder() {
      const scannedRef = String(this.ref || '').trim()
      if (!scannedRef) return

      // Duplicate check
      const alreadyAdded = this.exchangeScannedOrders.some(
        row => row.source_order_id === scannedRef
          || (row.source_order_display_id && row.source_order_display_id === scannedRef),
      )
      if (alreadyAdded) {
        this.playAudio(this.alreadyScanned)
        await this.withTitle('Ce colis a déjà été scanné')
        return
      }

      this.isScanningExchange = true
      try {
        const { data } = await axios.post('/api/exchange-vouchers/scan-exchange-order/', {
          order_id: scannedRef,
        })

        this.exchangeScannedOrders.push({
          source_order_id: data.source_order_id,
          source_order_display_id: scannedRef,
          generated_order: data.generated_order,
          generated_order_id: data.generated_order_id,
          exchange_voucher_id: data.exchange_voucher_id,
        })

        this.ref = ''
        this.playAudio(this.ok)
        this.showToast(
          'success',
          'top-center',
          `Colis retour #${data.generated_order_id} créé — Bon d'échange #${data.exchange_voucher_id}`,
        )
      } catch (error) {
        const code = error.response?.data?.code
        const msg = error.response?.data?.error || 'Erreur lors du scan du colis'
        if (code === 'already_scanned') {
          this.playAudio(this.alreadyScanned)
        } else {
          this.playAudio(this.notexist)
        }
        await this.withTitle(msg)
      } finally {
        this.isScanningExchange = false
      }
    },

    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        { position },
      )
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
